/**
 * TSR Sales Report — the pure pieces of its display, kept out of the 9,000-line
 * component so they can be tested on their own.
 */

/**
 * Reports-screen card → the element on the Sales Report it scrolls to.
 *
 * The cards were written against section names the page never had: only
 * "sales-trend" was an element id, and it is not drawn on the default Today
 * view. "Daily Sales Summary" now lands on the headline cards, "Sale Trend"
 * (subtitled Gross Profit vs. COGS) on the profit donut, "Top Items" on the top
 * five.
 */
export const SECTION_TARGETS = {
  'sales-trend': 'sales-summary',
  'gross-profit': 'profit-cogs-distribution',
  'top-items': 'top-5-items',
};

export const sectionTargetId = (section) => {
  if (section === null || section === undefined) return null;
  return SECTION_TARGETS[section] || section;
};

const num = (v) => Number(v || 0);
const numOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * The /reports/summary answer, as the page keeps it.
 *
 * Everything the page READS must be here: the fields this used to drop
 * (unmapped_*, the estimated-cost figures, display_sales_basis) left every note
 * built on them dead, on screen and in the export that is handed this object.
 * Optional figures stay null when absent — unknown is not zero.
 */
export function normalizeSummary(r = {}) {
  return {
    total_sales: num(r.total_sales),
    total_orders: num(r.total_orders),
    gross_profit: num(r.gross_profit),
    display_gross_profit: r.display_gross_profit === null ? null : num(r.display_gross_profit),
    net_profit: num(r.net_profit),
    display_net_profit: r.display_net_profit === null ? null : num(r.display_net_profit),
    avg_order_value: num(r.avg_order_value),
    total_wastage: num(r.total_wastage),
    total_cogs: num(r.total_cogs),
    display_total_cogs: r.display_total_cogs === null ? null : num(r.display_total_cogs),
    item_breakdown: r.item_breakdown || [],
    has_estimated_items: r.has_estimated_items || false,
    estimated_item_count: num(r.estimated_item_count),
    has_zero_cogs_items: r.has_zero_cogs_items || false,
    zero_cogs_item_count: num(r.zero_cogs_item_count),
    has_unavailable_cogs_items: r.has_unavailable_cogs_items || false,
    // A2. Blanking a total is driven by all_cogs_unavailable. An answer cached
    // under the previous shape has none, and treating that absence as false
    // would un-blank a genuinely unpriceable day — so fall back to the old flag.
    all_cogs_unavailable: r.all_cogs_unavailable ?? (r.has_unavailable_cogs_items || false),
    excluded_cogs_item_count: num(r.excluded_cogs_item_count),
    excluded_cogs_revenue: num(r.excluded_cogs_revenue),
    excluded_cogs_items: r.excluded_cogs_items || [],
    // C9: { sharePct, warn, threshold, message }, or null.
    estimated_cogs_share: r.estimated_cogs_share || null,

    // ── Previously dropped ──
    // "incl. ₹X not in menu" on the Total Sales card, and its estimated cost.
    unmapped_revenue: num(r.unmapped_revenue),
    unmapped_item_count: num(r.unmapped_item_count),
    unmapped_cogs: numOrNull(r.unmapped_cogs),
    unmapped_cogs_estimated: Boolean(r.unmapped_cogs_estimated),
    unmapped_cost_ratio: numOrNull(r.unmapped_cost_ratio),
    // The sales the profit % is taken over (costed sales), when narrower than total.
    display_sales_basis: numOrNull(r.display_sales_basis),
    // "~ includes ₹X estimated cost across N items" notes.
    has_estimated_cogs_items: Boolean(r.has_estimated_cogs_items),
    estimated_cogs_amount: num(r.estimated_cogs_amount),
    estimated_cogs_item_count: num(r.estimated_cogs_item_count),

    // A business with its own Gross Profit formula (backend utils/grossProfitFormula):
    // 'sales_cogs_discount' = every channel's sales − COGS − platform discounts. Absent for
    // everyone else, and every figure above is then the house definition.
    gross_profit_formula: r.gross_profit_formula || null,
    in_house_sales: numOrNull(r.in_house_sales),
    online_sales: numOrNull(r.online_sales),
    online_cogs: numOrNull(r.online_cogs),
    total_discount: numOrNull(r.total_discount),
  };
}

/** True when the summary is under the Sales − COGS − Discounts formula. */
export const isSalesCogsDiscount = (summary = {}) => summary.gross_profit_formula === 'sales_cogs_discount';

const present = (v) => v !== null && v !== undefined;

/**
 * Sales split into cost of goods, wastage and the profit left — the donut, its
 * legend and the PDF/Excel "Gross Profit vs. Manufacturing Cost".
 *
 * The profit is the Gross Profit CARD's figure: net of wastage, by the same
 * fallback chain (display_net_profit → net_profit → display_gross_profit →
 * gross_profit). The donut used the before-wastage figure under the same label,
 * so one label carried two numbers and profit% + cost% + waste% passed 100.
 *
 * Shares are over the costed sales (display_sales_basis) when the backend gives
 * one: revenue nothing could cost is in neither the cost nor the profit, so
 * dividing by the whole bill would leave the three short of 100.
 */
export function profitSplit(summary = {}) {
  if (isSalesCogsDiscount(summary)) {
    // Sales − COGS − Discounts: the three wedges are cost, what the platforms took, and what
    // is left. Wastage is not part of this figure, so it is not a wedge of it.
    const sales = num(summary.total_sales);
    const cogs = num(summary.total_cogs);
    const discount = num(summary.total_discount);
    const profit = present(summary.gross_profit) ? num(summary.gross_profit) : sales - cogs - discount;
    const share = (v) => (sales > 0 ? (v / sales) * 100 : 0);
    return {
      sales, basis: sales, cogs, waste: 0, discount, profit,
      pct: { profit: share(profit), cogs: share(cogs), waste: 0, discount: share(discount) },
    };
  }
  const sales = num(summary.total_sales);
  const basis = present(summary.display_sales_basis) ? num(summary.display_sales_basis) : sales;
  const cogs = present(summary.display_total_cogs) ? num(summary.display_total_cogs) : num(summary.total_cogs);
  const waste = num(summary.total_wastage);
  const reported = [summary.display_net_profit, summary.net_profit, summary.display_gross_profit, summary.gross_profit]
    .find(present);
  const profit = present(reported) ? num(reported) : basis - cogs - waste;
  const share = (v) => (basis > 0 ? (v / basis) * 100 : 0);
  return {
    sales, basis, cogs, waste, profit,
    pct: { profit: share(profit), cogs: share(cogs), waste: share(waste) },
  };
}

/**
 * The profit-vs-cost donut's wedges — ONE list for both the chart's data and
 * its colours (they were two hand-kept copies that had to stay index-aligned).
 *
 * A loss gets NO wedge: the wedges are shares of sales, and in a loss the cost
 * and wastage already exceed the sales, so a "loss" slice beside them drew a
 * proportion that does not exist. The legend states the loss instead.
 */
export function donutSegments(summary = {}, expenses = {}) {
  const split = profitSplit(summary);
  const totalSales = num(summary.total_sales);
  const totalWastage = num(summary.total_wastage);
  const wedge = (key, name, value, color) => ({ key, name, value, actualValue: value, color });

  if (summary.all_cogs_unavailable && totalSales > 0) {
    return [{ key: 'cogs-unavailable', name: 'COGS Unavailable', value: 1, actualValue: 0, color: '#E0E0E0' }];
  }
  const segs = [];
  if (totalSales > 0) {
    if (split.profit > 0.01) segs.push(wedge('profit', 'Gross Profit', split.profit, '#34c759'));
    if (split.cogs > 0.01) segs.push(wedge('cogs', 'COGS (Manufacturing Cost)', split.cogs, '#FF9500'));
    if (num(split.discount) > 0.01) segs.push(wedge('discount', 'Discounts (delivery platforms)', split.discount, '#AF52DE'));
  }
  // Under Sales − COGS − Discounts wastage is not part of the split.
  if (!isSalesCogsDiscount(summary) && totalWastage > 0.01) segs.push(wedge('waste', 'Waste & Losses', totalWastage, '#FF3B30'));
  if (totalSales === 0) {
    const stockIn = num(expenses.total_expenses);
    if (stockIn > 0.01) segs.push(wedge('expense', 'Expenses (Stock-In)', stockIn, '#FF9500'));
  }
  if (segs.length === 0) segs.push({ key: 'nodata', name: 'No Data', value: 1, actualValue: 0, color: '#E0E0E0' });
  return segs;
}

/** Monday 00:00 (local) of the week containing `date` — "This Week" starts here, as the card does. */
export function weekStartMonday(date = new Date()) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dow = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - dow);
  return d;
}

/**
 * Which break-even target the Contribution card shows. ONE rule for both the
 * figure and its label — they used to be decided separately, so a picked month
 * showed the monthly target under "Daily Contribution".
 */
export function contributionKind({ selectedPeriod, selectedDate, selectedWeek } = {}) {
  if (selectedDate && selectedDate.isMonthSelection) return 'monthly';
  // A range picked in the calendar is measured against its own days: one day
  // is a day, several are 'range' (the daily target × the days). Only the
  // week arrows select a week.
  if (selectedWeek && selectedWeek.isCustomRange) return selectedWeek.isSingleDay ? 'daily' : 'range';
  if (selectedWeek) return 'weekly';
  if (selectedDate) return 'daily';
  switch (selectedPeriod) {
    case 'This Week': return 'weekly';
    case 'This Month': return 'monthly';
    case 'This Year': return 'yearly';
    default: return 'daily';
  }
}

export const contributionLabelKey = (state) => `reports.${contributionKind(state)}Contribution`;

const localDay = (v) => {
  if (v instanceof Date) return new Date(v.getFullYear(), v.getMonth(), v.getDate());
  const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(v);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Days in a picked range, both ends included (never less than one). */
export function rangeDays(selectedWeek) {
  if (!selectedWeek) return 1;
  const start = localDay(selectedWeek.start);
  const end = localDay(selectedWeek.end || selectedWeek.start);
  const days = Math.round((end - start) / 86400000) + 1;
  return Number.isFinite(days) && days > 0 ? days : 1;
}

/** The break-even figure the card shows, from a daily/weekly/monthly/yearly breakdown. */
export function contributionTarget(breakdown = {}, state = {}) {
  const kind = contributionKind(state);
  if (kind === 'range') return (Number(breakdown.daily) || 0) * rangeDays(state.selectedWeek);
  return Number(breakdown[kind]) || 0;
}

/** The card's label — a translated period name, or the days a picked range covers. */
export function contributionLabel(t, state = {}) {
  const kind = contributionKind(state);
  if (kind === 'range') return `Contribution (${rangeDays(state.selectedWeek)} days)`;
  return t(`reports.${kind}Contribution`);
}

/**
 * The date whose month the break-even target is spread over. The month picker
 * hands the page `{ year, month, isMonthSelection }`, not a Date, and passing
 * that straight to the breakdown (which calls getFullYear) threw — so saving
 * the target while a month was picked never saved.
 */
export function bepBasisDate(selectedDate, selectedWeek) {
  if (selectedDate && selectedDate.isMonthSelection) return new Date(selectedDate.year, selectedDate.month, 1);
  // A picked range is spread over the month it starts in.
  if (selectedWeek && selectedWeek.isCustomRange && selectedWeek.start) return localDay(selectedWeek.start);
  if (selectedDate instanceof Date) return selectedDate;
  return new Date();
}
